import { setDistinctIdCookie } from '@deque/distinct-id-client';
import removeTrailingSlash from 'remove-trailing-slash';
import { type EntryPath } from '~/app/pages/AxeMCPServerSetup/flow';
import { type Selections } from '~/app/pages/AxeMCPServerSetup/guide';
import { type InitParams, type SendAmplitudeEventParams } from './Analytics';
import CrossProductAnalytics from './CrossProductAnalytics';
import { type LandingProperties } from './utils/getLandingProperties';

export type StepValues = { entry: EntryPath } & Selections;

export type GettingStartedStep = keyof StepValues;

export type StepCompleted<K extends GettingStartedStep = GettingStartedStep> = {
  [P in K]: { step: P; value: StepValues[P] };
}[K];

export enum AxeMCPServerAmplitudeEvents {
  VIEW_GETTING_STARTED = 'axe-mcp-server:getting-started:view',
  STEP_COMPLETED = 'axe-mcp-server:getting-started:step-completed',
  GUIDE_DOWNLOADED = 'axe-mcp-server:getting-started:guide-downloaded'
}

const FUNNEL_EVENTS: readonly string[] = Object.values(
  AxeMCPServerAmplitudeEvents
);

/**
 * The project tracks the getting-started funnel only, so nothing else this
 * instance inherits may reach it: no SDK page-load tracking, and no sign-up
 * or cross-product events.
 */
export default class AxeMCPServerAnalytics extends CrossProductAnalytics<AxeMCPServerAmplitudeEvents> {
  /** Shares an Amplitude project with the local and remote MCP servers; see docs/mcp-surfaces.md. */
  protected persistentEventProperties: Record<string, unknown> = {
    mcp_surface: 'web'
  };

  constructor(params: InitParams) {
    super({
      ...params,
      config: {
        ...params.config,
        includeReferrer: false,
        includeUtm: false,
        includeGclid: false
      }
    });
  }

  protected sendAmplitudeEvent(
    params: SendAmplitudeEventParams<AxeMCPServerAmplitudeEvents>
  ) {
    if (!FUNNEL_EVENTS.includes(params.event)) {
      return;
    }
    return super.sendAmplitudeEvent(params);
  }

  /** EnterprisesProvider calls this for every user, including those without the product. */
  public setEnterpriseId() {}

  /** Writes the shared cookie but keeps the URL, which can carry `redirect_uri` and invite metadata, off the user. */
  protected async processDistinctIdCookieData(
    href: string,
    defaultOriginalUtmSource: string
  ) {
    const cookieData = this.distinctIdCookie?.data;
    if (cookieData?.original_landing_page || cookieData?.original_utm_source) {
      return {
        original_landing_page: cookieData.original_landing_page as string,
        original_utm_source: cookieData.original_utm_source as string
      };
    }
    const data = {
      original_landing_page: href,
      original_utm_source: defaultOriginalUtmSource
    };
    const host = removeTrailingSlash(window.appConfig.API_SERVER_URL || '');
    await setDistinctIdCookie({ data }, { host });
    return data;
  }

  /**
   * Amplitude restores the last identified user from its cookie, and this
   * project skips users without the product, so anyone else's identity has
   * to go. A new device id stops Amplitude tying it back to that user.
   */
  public forgetUser(keepUserId?: string) {
    const { userId } = this.amplitude.options;
    if (!userId || userId === keepUserId) {
      return;
    }
    this.amplitude.setUserId(null);
    this.amplitude.regenerateDeviceId();
  }

  public viewGettingStarted(data: LandingProperties) {
    return this.sendAmplitudeEvent({
      event: AxeMCPServerAmplitudeEvents.VIEW_GETTING_STARTED,
      data
    });
  }

  public stepCompleted<K extends GettingStartedStep>(data: StepCompleted<K>) {
    return this.sendAmplitudeEvent({
      event: AxeMCPServerAmplitudeEvents.STEP_COMPLETED,
      data
    });
  }

  public guideDownloaded(data: { guide: EntryPath }) {
    return this.sendAmplitudeEvent({
      event: AxeMCPServerAmplitudeEvents.GUIDE_DOWNLOADED,
      data
    });
  }
}
